import React,{useState,useEffect} from 'react';
import {Outlet,useLocation} from 'react-router-dom';
import {useAuth} from '../../context/AuthContext';
import {useTheme} from '../../context/ThemeContext';
import {useLang} from '../../i18n';
import Sidebar from './Sidebar';
import LanguageSwitcher from '../LanguageSwitcher';
import UserProfileModal from '../UserProfileModal';
import {LogOut,Menu,X,Sun,Moon} from 'lucide-react';
import {Badge} from '../common';

export default function AppLayout(){
  const{user,logout}=useAuth();
  const{isDark,toggle:toggleTheme}=useTheme();
  const{t}=useLang();
  const loc=useLocation();
  const[desktopCollapsed,setDesktopCollapsed]=useState(false);
  const[mobileOpen,setMobileOpen]=useState(false);
  const[isMobile,setIsMobile]=useState(()=>window.innerWidth<768);
  const[showProfile,setShowProfile]=useState(false);

  useEffect(()=>{
    const fn=()=>setIsMobile(window.innerWidth<768);
    window.addEventListener('resize',fn);
    return()=>window.removeEventListener('resize',fn);
  },[]);

  useEffect(()=>setMobileOpen(false),[loc.pathname]);
  useEffect(()=>{if(!isMobile)setMobileOpen(false);},[isMobile]);

  useEffect(()=>{
    document.body.style.overflow=(isMobile&&mobileOpen)?'hidden':'';
    return()=>{document.body.style.overflow='';};
  },[isMobile,mobileOpen]);

  const isPosRoute = loc.pathname === '/pos';

  // Avatar initials
  const initials=(user?.first_name?.[0]||'')+(user?.last_name?.[0]||'');

  return(
    <div className="app-layout">

      {/* Backdrop overlay */}
      <div
        onClick={()=>setMobileOpen(false)}
        style={{display:isMobile&&mobileOpen?'block':'none',position:'fixed',inset:0,zIndex:299,background:'rgba(0,0,0,.6)',backdropFilter:'blur(2px)'}}
      />

      {/* Sidebar — hidden entirely on POS route */}
      {!isPosRoute&&<Sidebar
        collapsed={desktopCollapsed}
        mobileOpen={mobileOpen}
        isMobile={isMobile}
        onToggle={()=>{ if(isMobile) setMobileOpen(v=>!v); else setDesktopCollapsed(v=>!v); }}
      />}

      <div className="main-content" style={isPosRoute?{width:'100%',marginLeft:0}:{}}>
        <header className="top-bar" style={{display:isPosRoute?'none':'flex'}}>

          {/* Hamburger */}
          <button
            onClick={()=>{ if(isMobile) setMobileOpen(v=>!v); else setDesktopCollapsed(v=>!v); }}
            aria-label="Toggle menu"
            style={{background:'none',border:'none',cursor:'pointer',color:'var(--tm)',padding:6,marginRight:8,display:'flex',alignItems:'center',borderRadius:8,flexShrink:0,transition:'all .15s'}}
            onMouseEnter={e=>e.currentTarget.style.background='var(--s2)'}
            onMouseLeave={e=>e.currentTarget.style.background='none'}
          >
            {isMobile&&mobileOpen?<X size={20}/>:<Menu size={20}/>}
          </button>

          {/* User info */}
          <div style={{flex:1,minWidth:0,overflow:'hidden',display:'flex',alignItems:'center',gap:6}}>
            <span style={{fontSize:13,fontWeight:700,whiteSpace:'nowrap'}}>{user?.first_name} {user?.last_name}</span>
            <Badge variant="primary">{user?.role?.name}</Badge>
            {user?.branch&&!isMobile&&<Badge variant="accent">{user.branch.name}</Badge>}
          </div>

          {/* Right actions */}
          <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
            <LanguageSwitcher/>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={isDark?'Switch to Light Mode':'Switch to Dark Mode'}
              style={{background:'none',border:'1px solid var(--border)',cursor:'pointer',color:'var(--tm)',padding:6,display:'flex',alignItems:'center',borderRadius:8,transition:'all .15s'}}
              onMouseEnter={e=>{e.currentTarget.style.background='var(--s2)';e.currentTarget.style.color='var(--text)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='var(--tm)';}}
            >
              {isDark?<Sun size={15}/>:<Moon size={15}/>}
            </button>

            {/* Profile avatar */}
            <button
              onClick={()=>setShowProfile(true)}
              title="My Profile"
              style={{width:32,height:32,borderRadius:8,background:'var(--pm)',border:'2px solid var(--primary)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,color:'var(--primary)',flexShrink:0,transition:'all .15s'}}
              onMouseEnter={e=>{e.currentTarget.style.background='var(--primary)';e.currentTarget.style.color='#fff';}}
              onMouseLeave={e=>{e.currentTarget.style.background='var(--pm)';e.currentTarget.style.color='var(--primary)';}}
            >
              {initials||'U'}
            </button>

            {/* Sign out */}
            <button className="btn btn-ghost btn-sm" onClick={logout} style={{gap:5}}>
              <LogOut size={14}/>
              {!isMobile&&<span>{t('common.signOut')}</span>}
            </button>
          </div>

        </header>
        <main className="page-content" style={{padding:isPosRoute?0:undefined}}><Outlet/></main>
      </div>

      {/* Profile modal */}
      {showProfile&&<UserProfileModal onClose={()=>setShowProfile(false)}/>}
    </div>
  );
}
